import { AttributesList, AttributesListItem, createAttributesItem } from 'src/utils/createAttributesItem.ts';
import { filterEmpty as filterEmptyOriginal } from 'src/utils/filterEmpty.ts';
import { SensorProduct } from 'src/sensors/list/SensorsPageMapping.ts';
import { convertFS } from 'src/sensors/list/convertFS.ts';
import { AttributesAliases } from 'src/generated/dict.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { FlowRangeFields } from 'src/sensors/FlowUnitsRadioButtons/FlowUnitsOptions.ts';
import { isRoomSensor } from 'src/modules/common/utils/isRoomSensor';
import { convertProbeLength } from 'src/sensors/list/convertProbeLength';
import { isFlowSensor } from 'src/modules/common/utils/isFlowSensor.ts';

const NO = '@no';

export function inner(
    product: SensorProduct,
    isAmericas: boolean,
    isImperial: boolean,
    selectedFlowRange: FlowRangeFields,
): [AttributesList, AttributesList] {
    const displayTypeAlias = isRoomSensor(product.SensorApplication)
        ? AttributesAliases.DisplayType
        : AttributesAliases.displayTypeSAS;
    const displayTypeValue = isRoomSensor(product.SensorApplication)
        ? product.DisplayType
        : product.displayTypeSAS;

    const ApplicaitonOrDN: AttributesListItem = isFlowSensor(product.MeasuredValues) ? [
        AttributesAliases.dn,
        product.DN,
    ] : [
        AttributesAliases.SensorApplication,
        product.SensorApplication,
    ];

    const attrs: [AttributesList, AttributesList] = [
        [
            [MESSAGES.Description, product.ProductSubHeader],
            ...createAttributesItem(
                displayTypeAlias,
                displayTypeValue || { raw: [NO], localized: [NO] },
            ),
            ApplicaitonOrDN,
            [AttributesAliases.OutputSignal, product.OutputSignal],
        ],
        [
            [AttributesAliases.MeasuredValues, product.MeasuredValues],
            [
                AttributesAliases.ProbeLength,
                isAmericas ? product.ProbeLength : convertProbeLength(product.ProbeLength, isImperial),
            ],
            [AttributesAliases.MeasuringRangeCO2, product.MeasuringRangeCO2],
            ...(isImperial ? [
                [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
            ] : [
                [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature],
            ]) as AttributesList,
            [AttributesAliases.MeasuringRangeRH, product.MeasuringRangeRH],
            [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures],
            [
                isImperial ? AttributesAliases.i_flow : AttributesAliases.m_flow,
                isImperial ? (product.i_flow || convertFS(product.m_flow, isImperial)) : product.m_flow,
            ],
            ...(selectedFlowRange && selectedFlowRange.length > 0 ? [
                [AttributesAliases[selectedFlowRange], product[selectedFlowRange]],
            ] : []) as AttributesList,
            [AttributesAliases.pressureAir, product.pressureAir],
            [AttributesAliases.certification, product.certification],
            [AttributesAliases.mountingType, product.mountingType],
        ]];

    return [attrs[0].filter(filterEmptyOriginal), attrs[1].filter(filterEmptyOriginal)];
}

const filterEmpty = filterEmptyOriginal;

const NO_VALUE = { raw: ['@no'], localized: ['@no'] };

export function prepareSensorAttributes(
    product: SensorProduct,
    isAmericas: boolean,
    isImperial: boolean,
    selectedFlowRange: FlowRangeFields,
): [AttributesList, AttributesList] {
    switch (true) {
        case /^01APS-.*\.1/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.pressureAir, product.pressureAir], // 8387 - 8386?
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.PackagingQuantity, product.PackagingQuantity], // 6573
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01APS-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.pressureAir, product.pressureAir], // 8387 - 8386?
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22ADP-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.pressureAir, product.pressureAir], // 8387 - 8386?
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22WDP-/.test(product.Label):
        case /^22WP-/.test(product.Label):
        case /^22PDP-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.MeasuringRangePressureBar, product.MeasuringRangePressureBar], // 8028
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22DCK-/.test(product.Label):
        case /^22DCM-/.test(product.Label):
        case /^22DCV-/.test(product.Label):
        case /^22DC-/.test(product.Label):
        case /^22DTC-/.test(product.Label):
        case /^22DTM-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01RT-/.test(product.Label): {
            // 7906 (new), 8060 (new)
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^CR24/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22RT/.test(product.Label):
        case /^P-22RT/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.DisplayType, product.DisplayType], // 7977
                    [AttributesAliases.io, product.io], // 201
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.TargetValue, product.TargetValue], // 2776
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^P-01RT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.Potentiometer, product.Potentiometer], // Potentiometer, 8529

                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }

        case /^22PEM-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.DN, product.DN], // 15
                    [AttributesAliases.qp, product.qp], // 8729

                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.PN, product.PN], // 528
                    [AttributesAliases.certification, product.certification], // 504
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22PE-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.DN, product.DN], // 15
                    [AttributesAliases.qp, product.qp], // 8729

                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.PN, product.PN], // 528
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22PF-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.DN, product.DN], // 15
                    [AttributesAliases.fsM3hNoWeb, product.fsM3hNoWeb], // Fs [m3/h], 2811

                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.PN, product.PN], // 528
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01HT-10/.test(product.Label):
        case /^01ATS-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013

                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01CT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.CableLength, product.CableLength], // 93
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01DH-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.MeasuringRangeRH, product.MeasuringRangeRH], // 7797
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01HT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01MT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01PT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01ST-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.CableLength, product.CableLength], // 93
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01UT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^20DTS-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.Capillary, product.Capillary], // 8600
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.AdjustmentRange, product.AdjustmentRange], // 8711
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22CT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.CableLength, product.CableLength], // 93
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22DTH-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.Medium, product.Medium], // 6673
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^01DT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                    [AttributesAliases.OutputSignalPassiveTemperature, product.OutputSignalPassiveTemperature], // 8060
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22DT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.displayTypeSAS, product.displayTypeSAS ?? NO_VALUE], // 592
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22HH-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.CableLength, product.CableLength], // 93
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22HL-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.DetectionCableLength, product.DetectionCableLength], // 8200
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22HTH-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.CableLength, product.CableLength], // 93
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.mountingType, product.mountingType], // 8534
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22HT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.mountingType, product.mountingType], // 8534
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22MT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.ProbeLength, product.ProbeLength], // 7984
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.mountingType, product.mountingType], // 8534
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^22UT-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        case /^EXT-J-/.test(product.Label): {
            const attrs: [AttributesList, AttributesList] = [
                [
                    [AttributesAliases.ProductSubHeader, product.ProductSubHeader], // 886
                    [AttributesAliases.SensorApplication, product.SensorApplication], // 7844
                    ...(isImperial ? [
                        [AttributesAliases.i_MeasuringRangeTemperature, product.i_MeasuringRangeTemperature],
                    ] : [
                        [AttributesAliases.m_MeasuringRangeTemperature, product.m_MeasuringRangeTemperature], // 7906
                    ]) as AttributesList,
                    [AttributesAliases.Capillary, product.Capillary], // 8600
                ],
                [
                    [AttributesAliases.MeasuredValues, product.MeasuredValues], // 7786
                    [AttributesAliases.OutputSignal, product.OutputSignal], // 7373
                    [AttributesAliases.AdditionalFeatures, product.AdditionalFeatures], // 8013
                ],
            ];

            return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
        }
        default: {
            return inner(
                product,
                isAmericas,
                isImperial,
                selectedFlowRange,
            );
        }
    }
}
